import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, CheckCircle2, ExternalLink, Info, AlertTriangle, Sparkles, Download, LogIn } from "lucide-react";
import { LoyalPrimusLogo } from "@/components/BrandLogo";
import AnnouncementCard from "@/components/AnnouncementCard";

const STEPS = [
  { n: 1, title: "Register with LOYAL PRIMUS", desc: "Go to link and register a new client account.", pdf: { url: "https://customer-assets-39nsmqrw.emergentagent.net/job_trading-secret-app/artifacts/63wgn7rt_Registration%20Step.pdf", filename: "Registration-Step.pdf", label: "Registration guide" } },
  { n: 2, title: "Complete Verification", desc: "Complete broker KYC / document verification.", pdf: { url: "https://customer-assets-39nsmqrw.emergentagent.net/job_trading-secret-app/artifacts/yebf7d97_Verification%20Step.pdf", filename: "Verification-Step.pdf", label: "Verification guide" } },
  { n: 3, title: "Make Deposit", desc: "Deposit minimum $50 to activate your account." },
  { n: 4, title: "Return with Client ID", desc: "Come back here with Loyal Primus Client ID + registered email." },
];

const STATES_MY = ["Johor","Kedah","Kelantan","Melaka","Negeri Sembilan","Pahang","Perak","Perlis","Pulau Pinang","Sabah","Sarawak","Selangor","Terengganu","W.P. Kuala Lumpur","W.P. Labuan","W.P. Putrajaya"];

function BrokerRegister() {
  const [confirmed, setConfirmed] = useState({ s1: false, s2: false, s3: false, s4: false });
  const [stage, setStage] = useState("checklist"); // checklist | form | done
  const [form, setForm] = useState({ loyal_primus_client_id: "", email: "", telegram_username: "" });
  const [busy, setBusy] = useState(false);
  const allChecked = confirmed.s1 && confirmed.s2 && confirmed.s3 && confirmed.s4;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.loyal_primus_client_id || !form.email || !form.telegram_username) {
      toast.error("Sila lengkapkan semua fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Submitted! Menunggu admin verification.");
      setStage("done");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-8" data-testid="broker-register-section">
      <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-slate-200 bg-gradient-to-br from-white to-slate-50">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Register with</span>
                <LoyalPrimusLogo height={30} />
              </div>
              <p className="mt-3 text-slate-600 max-w-xl leading-relaxed">
                Register dengan broker rasmi kami untuk unlock semua features.
              </p>
            </div>
            <a
              data-testid="loyal-primus-cta"
              href="https://client.loyalprimus.net/links/go/750"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] text-white font-display font-semibold px-6 py-3.5 rounded-full transition-colors"
              style={{ transition: "background-color 0.2s ease, transform 0.2s ease" }}
            >
              Register at Loyal Primus
              <ExternalLink size={16} className="group-hover:translate-x-0.5" style={{ transition: "transform 0.2s ease" }} />
            </a>
          </div>
        </div>

        {stage === "checklist" && (
          <div className="p-6 md:p-8">
            <div className="flex flex-col gap-3">
              {STEPS.map((s, idx) => (
                <div key={s.n} className="relative rounded-xl border border-slate-200 p-5 bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 grid place-items-center font-display font-bold text-[#00b4ff] shrink-0">
                      {s.n}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-slate-900">{s.title}</div>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                    </div>
                    {s.pdf && (
                      <a
                        href={s.pdf.url}
                        download={s.pdf.filename}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`step-${s.n}-pdf`}
                        className="hidden sm:inline-flex items-center gap-2 shrink-0 rounded-full border border-[#00b4ff] bg-white hover:bg-[#00b4ff] hover:text-white text-[#00b4ff] px-4 py-2 text-xs font-display font-semibold"
                        style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
                      >
                        <Download size={14} /> Download PDF
                      </a>
                    )}
                  </div>
                  {s.pdf && (
                    <a
                      href={s.pdf.url}
                      download={s.pdf.filename}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`step-${s.n}-pdf-mobile`}
                      className="sm:hidden mt-3 inline-flex items-center gap-2 rounded-full border border-[#00b4ff] bg-white hover:bg-[#00b4ff] hover:text-white text-[#00b4ff] px-4 py-2 text-xs font-display font-semibold"
                      style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
                    >
                      <Download size={14} /> Download {s.pdf.label}
                    </a>
                  )}
                  {idx < STEPS.length - 1 && (
                    <div className="absolute left-[36px] -bottom-3 w-px h-3 bg-slate-200" aria-hidden />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5 flex gap-3">
              <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 leading-relaxed">
                <span className="font-semibold">Confirm semua step</span> dan deposit sebelum click <span className="font-semibold">&ldquo;Continue to next page&rdquo;</span>.
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {[
                ["s1", "Sudah register di Loyal Primus"],
                ["s2", "Verification / KYC dah complete"],
                ["s3", "Deposit minimum $50 done"],
                ["s4", "Saya ada Client ID + registered email"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    data-testid={`confirm-${key}`}
                    checked={confirmed[key]}
                    onChange={(e) => setConfirmed((c) => ({ ...c, [key]: e.target.checked }))}
                    className="w-5 h-5 rounded border-slate-300 text-[#00b4ff] focus:ring-[#00b4ff]"
                  />
                  <span className="text-sm text-slate-700 group-hover:text-slate-900" style={{ transition: "color 0.2s ease" }}>{label}</span>
                </label>
              ))}
            </div>

            <button
              data-testid="continue-to-form-btn"
              disabled={!allChecked}
              onClick={() => setStage("form")}
              className="mt-8 inline-flex items-center gap-2 bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-display font-semibold px-6 py-3.5 rounded-full transition-colors"
              style={{ transition: "background-color 0.2s ease" }}
            >
              Continue to next page <ArrowRight size={16} />
            </button>

            <p className="mt-4 text-xs text-slate-500">
              Kalau sudah ada Loyal Primus Client ID sebelum ini, tak perlu register lagi — terus{" "}
              <button data-testid="skip-to-form-btn" onClick={() => setStage("form")} className="text-[#00b4ff] font-semibold hover:underline">submit details di sini</button>.
            </p>
          </div>
        )}

        {stage === "form" && (
          <form onSubmit={submit} className="p-6 md:p-8 space-y-5 max-w-2xl" data-testid="broker-form">
            <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Submit your details</div>
            <h3 className="font-display text-2xl font-bold tracking-tight">Loyal Primus Details</h3>

            <div>
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Loyal Primus Client ID</label>
              <input
                data-testid="input-client-id"
                required
                value={form.loyal_primus_client_id}
                onChange={(e) => setForm({ ...form, loyal_primus_client_id: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff] font-mono-plex"
                placeholder="e.g. 750123"
              />
              <p className="mt-1 text-xs text-slate-500">Client ID ini akan menjadi password login anda.</p>
            </div>

            <div>
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Registered Email</label>
              <input
                data-testid="input-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="text-xs font-mono-plex tracking-widest uppercase text-slate-500">Telegram Username</label>
              <input
                data-testid="input-telegram"
                required
                value={form.telegram_username}
                onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#00b4ff]/40 focus:border-[#00b4ff]"
                placeholder="@yourname"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setStage("checklist")}
                className="px-5 py-3 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-semibold"
                style={{ transition: "background-color 0.2s ease" }}
              >
                Back
              </button>
              <button
                data-testid="submit-broker-form"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white font-display font-semibold px-6 py-3 rounded-full"
                style={{ transition: "background-color 0.2s ease" }}
              >
                {busy ? "Submitting…" : "Submit for verification"}
                <ArrowRight size={16} />
              </button>
            </div>

            <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-slate-600">
                Kalau sudah submit, anda boleh cuba <span className="font-semibold text-slate-900">login</span>.
              </div>
              <Link
                to="/login"
                data-testid="broker-form-login-btn"
                className="inline-flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-900 px-5 py-2.5 rounded-full text-sm font-semibold self-start sm:self-auto"
                style={{ transition: "background-color 0.2s ease" }}
              >
                <LogIn size={14} /> Log In
              </Link>
            </div>
          </form>
        )}

        {stage === "done" && (
          <div className="p-8 md:p-12">
            <div className="max-w-xl">
              <div className="w-12 h-12 rounded-full bg-[#00b4ff]/10 grid place-items-center">
                <CheckCircle2 size={22} className="text-[#00b4ff]" />
              </div>
              <h3 className="mt-4 font-display text-2xl font-bold tracking-tight">
                Waiting for Admin verification ✓
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Details submitted. Anda akan dinotify di bahagian Announcements sebaik sahaja admin approve.
                Semasa tunggu, boleh join community di Telegram.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  data-testid="join-telegram-cta"
                  href="https://t.me/tradingsecretmalaysia"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-full text-sm font-semibold"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  Join Telegram Community
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function AnnouncementFeed() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = async () => {
    if (!user) { setItems([]); return; }
    try {
      const { data } = await api.get("/announcements");
      setItems(data);
    } catch (err) { logDev(err); }
  };

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [user]);

  return (
    <section className="mt-14" data-testid="announcement-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-mono-plex text-slate-500">Feed</div>
          <h2 className="mt-1 font-display text-3xl font-bold tracking-tight">Announcements</h2>
        </div>
        {user && <div className="text-xs font-mono-plex tracking-widest text-slate-400 uppercase">{items.length} items</div>}
      </div>

      {!user && (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-slate-600">
          Register untuk terima announcements personal & signal alerts.
        </div>
      )}

      {user && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-slate-500 text-sm">
          Tiada announcement lagi. Signal & updates admin akan appear di sini.
        </div>
      )}

      <div className="grid gap-3">
        {items.map((a, i) => <AnnouncementCard key={a.id} a={a} index={i} />)}
      </div>
    </section>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const loggedIn = !!user;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16">
      {/* Hero */}
      <div className="relative">
        <div className="dot-grid absolute inset-0 -z-10 opacity-60" />
        {loggedIn ? (
          <>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Selamat datang, <span className="text-[#00b4ff]">{user.name || user.email.split("@")[0]}</span>.
            </h1>
            <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed text-base sm:text-lg">
              Semua announcement dan signal terkini akan appear di bawah.
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Trading <span className="text-[#00b4ff]">Secret</span> Apps
            </h1>
            <p className="mt-5 text-slate-600 max-w-2xl leading-relaxed text-base sm:text-lg">
              Follow simple step untuk register dan dapatkan access penuh kepada Apps kami.
            </p>
          </>
        )}

        {user?.role === "admin" && (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-mono-plex tracking-widest uppercase">
            <span className="live-dot" /> Logged in as ADMIN
          </div>
        )}
        {user && user.role !== "admin" && (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#00b4ff]/30 bg-[#00b4ff]/5 px-4 py-2 text-xs font-mono-plex tracking-widest uppercase text-[#00b4ff]">
            <CheckCircle2 size={12} /> Status: {user.status?.toUpperCase()}
          </div>
        )}
      </div>

      {!loggedIn && <BrokerRegister />}
      <AnnouncementFeed />
    </div>
  );
}
